import React, { useCallback, useEffect, useState } from 'react';

import { logger } from '@sentry/browser';
import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';

import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Container,
  GridLegacy as Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { toPromoBrand } from '~/consts/promoBrand';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useQuery from '~/hooks/useQuery';

import type { ChannelMarkupV2Internal } from '~/services/PromoService';
import { getChannelMarkupListV2 } from '~/services/PromoService';
import { customerPortalHost } from '~/services/common';

import dateFormatter from '~/utils/dateFormatter';

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 1,
};

const getColumns = ({
  tenant,
  handleEditChannelMarkup,
}: {
  tenant: App.Tenant;
  handleEditChannelMarkup: (row: ChannelMarkupV2Internal) => void;
}): Array<GridColDef<ChannelMarkupV2Internal>> => [
  {
    field: 'channel_markup_id',
    headerName: 'ID',
    ...sharedOpts,
  },
  {
    field: 'version',
    headerName: 'Version',
    renderCell: (params) => (
      <Typography sx={{ fontWeight: 600, textTransform: 'uppercase' }}>{params.row.version}</Typography>
    ),
    ...sharedOpts,
  },
  {
    field: 'rules',
    headerName: 'Rules',
    renderCell: (params) => {
      const conditions = params.row.rules?.conditions ?? [];
      const markups = params.row.rules?.markups ?? [];
      return (
        <Stack spacing={0.5}>
          <Typography>
            <b>Conditions</b> {conditions.length}
          </Typography>
          <Typography>
            <b>Markups</b> {markups.length}
          </Typography>
        </Stack>
      );
    },
    ...sharedOpts,
  },
  {
    field: 'link',
    headerName: 'Link',
    ...sharedOpts,
    renderCell: (params) => {
      const firstMarkup = params.row.rules?.markups?.[0];
      const query = {
        ...(firstMarkup?.region ? { region: firstMarkup.region } : {}),
        ...(firstMarkup?.currency ? { currency: firstMarkup.currency } : {}),
      };
      const queryString = qs.stringify(query);
      return (
        <Link href={`${customerPortalHost(tenant)}${queryString ? `?${queryString}` : ''}`}>Customer Portal Link</Link>
      );
    },
  },
  {
    field: 'created_at',
    headerName: 'Created At',
    renderCell: (params) => dateFormatter(params.row.created_at),
    ...sharedOpts,
  },
  {
    field: 'expires_at',
    headerName: 'Expires At',
    renderCell: (params) => dateFormatter(params.row.expires_at),
    ...sharedOpts,
  },
  {
    field: 'warnings',
    headerName: 'Warnings',
    renderCell: (params) => {
      const warnings = params.row.warnings ?? [];
      if (warnings.length === 0) {
        return <Typography>-</Typography>;
      }

      return (
        <Stack spacing={0.25}>
          {warnings.map((warning) => (
            <Typography key={warning.message}>{warning.message}</Typography>
          ))}
        </Stack>
      );
    },
    ...sharedOpts,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    renderCell: (params) => (
      <IconButton
        aria-label={`Edit channel markup ${params.row.channelMarkupId ?? ''}`.trim()}
        onClick={() => handleEditChannelMarkup(params.row)}
        size="small"
      >
        <EditIcon fontSize="small" />
      </IconButton>
    ),
    ...sharedOpts,
  },
];

type ChannelMarkupStatus = 'active' | 'expired' | 'all';

export default function ChannelMarkupV2ListPage() {
  const query = useQuery();
  const { enqueueSnackbar } = useSnackbar();
  const { push } = useAppNavigation();
  const [data, setData] = useState<Array<ChannelMarkupV2Internal>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pageNum, setPageNum] = useState<number>(query.get('page_num') ? Number(query.get('page_num')) : 0);
  const [pageSize, setPageSize] = useState<number>(query.get('page_size') ? Number(query.get('page_size')) : 10);
  const [totalCount, setTotalCount] = useState(0);
  const { tenant } = useCurrentTenant();
  const [status, setStatus] = useState<ChannelMarkupStatus>((query.get('status') as ChannelMarkupStatus) || 'all');

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum.toString(),
      page_size: pageSize.toString(),
      status,
    });
    push({
      search: `?${searchParams}`,
    });
  }, [push, pageNum, pageSize, status]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      const {
        result: { channelMarkupList, total },
      } = await getChannelMarkupListV2({
        page: pageNum,
        pageSize,
        brand: toPromoBrand(tenant.brand),
        status,
      });
      setData(channelMarkupList ?? []);
      setTotalCount(Number(total));
      setQueryParams();
    } catch (error) {
      logger.error('Channel Markup v2 fetch failed', error);
      enqueueSnackbar('Channel Markup v2 fetch failed', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar, pageNum, pageSize, setQueryParams, status, tenant.brand]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const onPageChange = useCallback(
    (page: number, newPageSize: number) => {
      setPageNum(page);
      setPageSize(newPageSize);
      query.set('page_num', page.toString());
      query.set('page_size', newPageSize.toString());
      window.history.pushState({}, '', `${window.location.pathname}?${query.toString()}`);
    },
    [query],
  );

  const dataRows = data.map((row, index) => ({
    id: row.channelMarkupId ?? index,
    ...row,
  }));

  const handleEditChannelMarkup = useCallback(
    (row: ChannelMarkupV2Internal) => {
      push(`/marketing/channel-markup-v2/${row.channelMarkupId}/edit`, { channelMarkup: row });
    },
    [push],
  );
  const columns = getColumns({ tenant, handleEditChannelMarkup });

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Channel Markup v2</title>
      </Helmet>

      <PageSubheader title={`Channel Markup v2 [${data?.length} of ${totalCount}]`} />

      <Grid container spacing={2} justifyContent="flex-end" alignItems="end">
        <Grid item>
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={(event) => setStatus(event.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </Select>
        </Grid>
        <Grid item>
          <Button variant="contained" color="primary" component={Link} href="/marketing/channel-markup-v2/create">
            New v2 Markup
          </Button>
        </Grid>
      </Grid>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize: newPageSize }) => onPageChange(page, newPageSize)}
          loading={isLoading}
          rows={dataRows}
          columns={columns}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: pageNum, pageSize }}
          rowHeight={100}
        />
      </Box>
    </Container>
  );
}
